import { createContext, useContext, useEffect, useState } from "react";
import type { FC, ReactNode } from "react";

interface Settings {
  direction?: "ltr" | "rtl";
  language: "en" | "pl";
  pinSidebar: boolean;
  theme?: string;
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  direction: "ltr",
  language: "en",
  pinSidebar: true,
  theme: "light",
};

export const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem("settings", JSON.stringify(settings));
};

export const restoreSettings = (): Settings | null => {
  let settings = null;

  try {
    const storedData = window.localStorage.getItem("settings");

    if (storedData) {
      const parsedSettings = JSON.parse(storedData);

      if (!parsedSettings.pinSidebar) {
        parsedSettings.pinSidebar = true;

        storeSettings(parsedSettings);
      }

      settings = JSON.parse(storedData);
    } else {
      settings = {
        direction: "ltr",
        language: "en",
        pinSidebar: true,
        theme: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
      };
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {},
});

export const SettingsProvider: FC<SettingsProviderProps> = props => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(initialSettings);

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, []);

  const saveSettings = (updatedSettings: Settings): void => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
      }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export const useSettings = () => useContext(SettingsContext);
